import { useCallback, useEffect, useRef, useState } from "react";
import { connect, useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router";
import { createPortal } from "react-dom";
import validator from "validator";
import { Eye, EyeOff, Loader2, X } from "lucide-react";
import { googleSignin, OtpLogin, sendOtp, signin } from "../../api/action/user.jsx";
import { findCompanyById, findPartnerCompanies } from "../../api/action/company.jsx";
import * as types from "../../api/action/types.jsx";
import { getDefaultCountryCode } from "../../config/locationConfig.js";
import keys from "../../config/keys.jsx";
import {
  clearConsoleHandoffData,
  clearLoginTransition,
  finalizeAuthenticatedSession,
  getConsoleHandoffData,
  isLoginTransitionActive,
  setLoginTransition,
  stageAuthenticatedSession,
  storeConsoleHandoffData,
} from "../../utils/loginTransition.jsx";
import { decodeBase64, encodeBase64 } from "./encryptPassword.jsx";
import "../../styles/retailAuth.css";
import { GOOGLE_SIGNIN_CANCELLED, isNativeMobileApp, signInWithGoogleNative } from "../../utils/googleAuth.js";
import GoogleAuthButton from "../../components/GoogleAuthButton.jsx";
import { useAuthSession } from "../../auth/useAuthSession.js";
import { SESSION_EXPIRED_MESSAGE } from "../../auth/sessionMessages.js";
import FlexLogin from "../LandingPage/FlexOfficeDomain/flex-new/components/FlexLogin.jsx";
import {
  clearPendingNavigation,
  consumePendingNavigation,
  getPendingNavigation,
} from "../../utils/pendingNavigation.js";

const localConsoleDomain = keys?.localConsoleDomain || "";
const domain = keys?.domain || "";
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_OAUTH_CLIENT_ID || "";
const CONSOLE_ROLES = ["ADMIN", "INSTRUCTOR", "TEACHING_ASSISTANT", "SUPER_ADMIN"];
const IN_APP_ROLES = ["STUDENT", "CUSTOMER", "APPLICANT", "CANDIDATE"];
const DOMAIN_SITE_ROLES = {
  EDUCATION: ["STUDENT"],
  INSTITUTION: ["APPLICANT"],
  ASSESSMENT: ["CANDIDATE"],
  FLEX_OFFICE: ["CUSTOMER"],
  RETAIL: ["CUSTOMER"],
  SERVICES: ["CUSTOMER"],
};
const VALID_DOMAIN_TYPES = new Set([
  "KRUMAGO",
  "RETAIL",
  "ASSESSMENT",
  "EDUCATION",
  "INSTITUTION",
  "FLEX_OFFICE",
  "SERVICES",
  "WAAS",
  "IT_AND_CONSULTING",
  "IT",
  "COMMON",
]);

const normalizeUrl = (url) => String(url || "").trim().replace(/\/+$/, "");

const getConsoleBaseUrl = (companyUrl = "", prefix = "") => {
  const configuredCompanyUrl = normalizeUrl(companyUrl);
  if (configuredCompanyUrl) {
    return configuredCompanyUrl.includes(":3000")
      ? configuredCompanyUrl.replace(":3000", ":4000")
      : configuredCompanyUrl;
  }

  const { origin = "", protocol = "", hostname = "", port = "" } = window.location || {};
  if (protocol === "http:" && port === "3000") {
    return `http://${hostname}:4000`;
  }

  if (origin.includes(":3000")) {
    return origin.replace(":3000", ":4000");
  }

  if (localConsoleDomain) {
    return `http://${prefix ? `${prefix}.` : ""}${localConsoleDomain}`;
  }

  if (domain) {
    return `https://${prefix || "www"}.${domain}`;
  }

  return normalizeUrl(origin);
};
const getRoleLabel = (role) => {
  switch (role) {
    case "CUSTOMER": return "Customer";
    case "STUDENT": return "Student";
    case "ADMIN": return "Administrator";
    case "INSTRUCTOR": return "Instructor";
    case "TEACHING_ASSISTANT": return "Teaching Assistant";
    case "EMPLOYEE": return "Employee";
    case "SUPER_ADMIN": return "Super Admin";
    case "APPLICANT": return "Applicant";
    case "CANDIDATE": return "Candidate";
    default: return role;
  }
};

const getRoleDescription = (role, domainType) => {
  switch (role) {
    case "CUSTOMER": return "Continue to the user portal";
    case "STUDENT": return "Continue to your academy dashboard";
    case "ADMIN": return "Open the admin console";
    case "INSTRUCTOR": return "Open the instructor console";
    case "TEACHING_ASSISTANT": return "Open the teaching assistant console";
    case "SUPER_ADMIN": return "Open the super admin console";
    case "EMPLOYEE": return domainType === "RETAIL" ? "Choose console or POS access" : "Continue as employee";
    case "APPLICANT": return "Continue to your application profile";
    case "CANDIDATE": return "Continue to your candidate workspace";
    default: return "Continue with this role";
  }
};
const normalizeDomainType = (domainType) => {
  const normalized = String(domainType || "").trim().toUpperCase();
  return VALID_DOMAIN_TYPES.has(normalized) ? normalized : "";
};

const getCompanyDomainType = (company) => normalizeDomainType(
  company?.domain?.type || company?.domainType || company?.companyDetails?.domain?.type
);

const normalizeRoles = (...roleSources) => {
  const normalizedRoles = roleSources
    .flatMap((source) => {
      if (Array.isArray(source)) {
        return source;
      }

      if (source === undefined || source === null || source === "") {
        return [];
      }

      return [source];
    })
    .map((role) => {
      if (typeof role === "string") {
        return role;
      }

      if (typeof role === "object") {
        return role?.name || role?.value || role?.role || role?.userRole || "";
      }

      return String(role || "");
    })
    .map((role) => role.trim().toUpperCase())
    .filter(Boolean);

  return [...new Set(normalizedRoles)];
};

const getLoginResponseData = (response) => (
  response?.data || response?.payload?.data || response?.userDetails || response?.payload?.userDetails || response
);

const getLoginResponseRoles = (response) => {
  const responseData = getLoginResponseData(response);

  return normalizeRoles(
    responseData?.userRoles,
    responseData?.userRole,
    responseData?.roles,
    responseData?.authorities,
    responseData?.userDetails?.userRoles,
    responseData?.userDetails?.userRole,
    responseData?.userDetails?.roles,
    responseData?.userDetails?.authorities,
    response?.userRoles,
    response?.userRole,
    response?.roles,
    response?.authorities,
    response?.userDetails?.userRoles,
    response?.userDetails?.userRole,
    response?.userDetails?.roles,
    response?.userDetails?.authorities,
    response?.payload?.userRoles,
    response?.payload?.userRole,
    response?.payload?.roles,
    response?.payload?.authorities,
    response?.payload?.userDetails?.userRoles,
    response?.payload?.userDetails?.userRole,
    response?.payload?.userDetails?.roles,
    response?.payload?.userDetails?.authorities
  ).map((role) => role.replace(/^ROLE_/, ""));
};

// A person can hold roles in more than one company (e.g. Partner Admin in a
// partner company and Student in the parent). Each option pairs a role with
// the specific company it belongs to, so the role-choice popup can tell them
// apart and load the correct company context after selection.
const getRoleOptionKey = (role, company) => (
  `${role}|${company?.primaryId || company?.companyId || ""}`
);

const isPartnerCompanyRecord = (company) => (
  String(company?.companyType || "").toUpperCase() === "PARTNER_ENTITY" || Boolean(company?.parentCompanyId)
);

const getCompanyContextLabel = (company) => {
  if (!company) return "";
  const name = company?.name || "";
  return isPartnerCompanyRecord(company)
    ? `Partner${name ? `: ${name}` : ""}`
    : `Parent${name ? `: ${name}` : ""}`;
};

// Flattens a list of company memberships (each { company, userRoles }) into
// individual { role, company } options, deduped by (role, companyId).
const flattenMembershipsToRoleOptions = (memberships = []) => {
  const options = memberships.flatMap((membership) => (
    normalizeRoles(membership?.userRoles).map((role) => ({ role, company: membership?.company || null }))
  ));

  const seenKeys = new Set();
  return options.filter((option) => {
    const key = getRoleOptionKey(option.role, option.company);
    if (seenKeys.has(key)) return false;
    seenKeys.add(key);
    return true;
  });
};

const normalizeRoleOptionForDomain = (option, activeDomainType) => {
  const role = normalizeRoles(option?.role)[0];
  if (!role) return null;

  const activeDomain = normalizeDomainType(activeDomainType);
  if (!activeDomain || activeDomain === "COMMON") {
    return { ...option, role };
  }

  if (CONSOLE_ROLES.includes(role)) {
    return { ...option, role };
  }

  const companyDomain = getCompanyDomainType(option?.company);
  if (companyDomain && companyDomain !== activeDomain) {
    return null;
  }

  const allowedSiteRoles = DOMAIN_SITE_ROLES[activeDomain];
  if (!allowedSiteRoles?.length) {
    return { ...option, role };
  }

  if (allowedSiteRoles.includes(role)) {
    return { ...option, role };
  }

  if (role === "CUSTOMER" && allowedSiteRoles.length === 1) {
    return { ...option, role: allowedSiteRoles[0] };
  }

  return null;
};

const normalizeRoleOptionsForDomain = (options = [], activeDomainType) => {
  const seenKeys = new Set();
  return options
    .map((option) => normalizeRoleOptionForDomain(option, activeDomainType))
    .filter(Boolean)
    .filter((option) => {
      const key = getRoleOptionKey(option.role, option.company);
      if (seenKeys.has(key)) return false;
      seenKeys.add(key);
      return true;
    });
};

const getLoginResponseToken = (response) => {
  const responseData = getLoginResponseData(response);

  return responseData?.accessConfirmationToken ||
    responseData?.accessToken ||
    responseData?.token ||
    responseData?.jwtResponse?.jwtToken ||
    response?.accessConfirmationToken ||
    response?.accessToken ||
    response?.token ||
    response?.jwtResponse?.jwtToken ||
    response?.payload?.accessConfirmationToken ||
    response?.payload?.accessToken ||
    response?.payload?.token ||
    response?.payload?.jwtResponse?.jwtToken ||
    "";
};

const getConsoleReturnUrl = () => {
  const returnUrl = new URLSearchParams(window.location.search || "").get("returnUrl") || "";
  if (!returnUrl.startsWith("/console/") || returnUrl.startsWith("//")) return "";
  return returnUrl;
};

const appendConsoleHandoffParams = (url, handoffData, fallbackCompanyCode) => {
  const separator = String(url || "").includes("?") ? "&" : "?";
  return `${url}${separator}email=${encodeURIComponent(handoffData.encryptedEmail)}&password=${encodeURIComponent(handoffData.encryptedPassword || "")}&companyCode=${encodeURIComponent(handoffData.companyCode || fallbackCompanyCode || "")}`;
};

const statusMessageClasses = {
  error: "border-red-200 bg-red-50 text-red-700",
  success: "border-emerald-200 bg-emerald-50 text-emerald-700",
  warning: "border-amber-200 bg-amber-50 text-amber-700",
  info: "border-blue-200 bg-blue-50 text-blue-700",
};

const StatusMessage = ({ severity = "error", children, onClose, className = "" }) => {
  if (!children) return null;

  return (
    <div
      role={severity === "error" ? "alert" : "status"}
      className={`flex items-start gap-3 rounded-lg border px-4 py-3 text-left text-sm ${statusMessageClasses[severity] || statusMessageClasses.info} ${className}`}
    >
      <span className="flex-1">{children}</span>
      {onClose && (
        <button
          type="button"
          aria-label="Close message"
          onClick={onClose}
          className="rounded-md p-0.5 opacity-70 transition hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-offset-1"
        >
          <X className="h-4 w-4" aria-hidden="true" />
        </button>
      )}
    </div>
  );
};

const PasswordToggleButton = ({ showPassword, onToggle, onMouseDown }) => (
  <button
    type="button"
    aria-label="toggle password visibility"
    onClick={onToggle}
    onMouseDown={onMouseDown}
    className="inline-flex h-9 w-9 items-center justify-center rounded-full text-gray-500 transition hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
  >
    {showPassword ? (
      <EyeOff className="h-5 w-5" aria-hidden="true" />
    ) : (
      <Eye className="h-5 w-5" aria-hidden="true" />
    )}
  </button>
);

const Login = ({ templateDomainType }) => {
  const { isAuthenticated, isLoggedIn } = useAuthSession();
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [emailError, setEmailError] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [state, setState] = useState({ open: false, message: "", severity: "error" });
  const handleGoogleCredentialResponseRef = useRef(null);
  const [user, setUser] = useState({ email: "", password: "" });
  const [showEmployeeChoiceModal, setShowEmployeeChoiceModal] = useState(false);
  const [showRoleChoiceModal, setShowRoleChoiceModal] = useState(false);
  const [selectedRole, setSelectedRole] = useState("");
  const [, setUserRoles] = useState([]);
  const [eligibleRoleOptions, setEligibleRoleOptions] = useState([]);
  const [selectedRoleOptionKey, setSelectedRoleOptionKey] = useState("");
  const [pendingLoginSession, setPendingLoginSession] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingAction, setLoadingAction] = useState(null);
  const [googleScriptLoaded, setGoogleScriptLoaded] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [otpCountdown, setOtpCountdown] = useState(0);

  const companyData = useSelector((state) => state?.companyReducer?.company || {});
  // Partner entities share the parent company's login — use parent company ID for auth context
  const isPartnerCompany = String(companyData?.companyType || '') === 'PARTNER_ENTITY';
  const companyPrimaryId = isPartnerCompany
    ? (companyData?.parentCompanyId || companyData?.primaryId || '')
    : (companyData?.primaryId || '');
  const companyUrl = isPartnerCompany ? '' : (companyData?.url || '');
  const countryCallingCode = getDefaultCountryCode(companyData);
  const domainType = normalizeDomainType(
    templateDomainType
    || getCompanyDomainType(companyData)
    || localStorage.getItem("domainType")
  );
  const isEducationDomain = domainType === "EDUCATION";
  const isRetailDomain = domainType === "RETAIL";

  const handleClickShowPassword = useCallback(() => {
    setShowPassword((prev) => !prev);
  }, []);

  const handleChange = useCallback(({ target: { name, value } = {} } = {}) => {
    if (name && value !== undefined) {
      setUser((prevUser) => ({ ...(prevUser || {}), [name]: value }));
      if (name === "email") {
        setEmailError(false);
      }
      setError("");
      setSuccess("");
    }
  }, []);

  const handleBlur = useCallback(({ target: { name, value } = {} } = {}) => {
    if (name === "email" && value !== undefined) {
      setEmailError(!validator.isEmail(value));
    }
  }, []);

  const handleMouseDownPassword = (event) => {
    event?.preventDefault();
  };

  const handlePhoneChange = useCallback((event) => {
    const value = event?.target?.value || "";
    const digitsOnly = value.replace(/\D/g, "").slice(0, 10);
    setPhoneNumber(digitsOnly);
    setError("");
    setSuccess("");
  }, []);

  const handleOtpChange = useCallback((event) => {
    const value = event?.target?.value || "";
    const digitsOnly = value.replace(/\D/g, "").slice(0, 6);
    setOtp(digitsOnly);
    setError("");
    setSuccess("");
  }, []);

  const validatePhone = useCallback((phone) => String(phone || "").trim().length === 10, []);

  const persistSessionForAppRole = useCallback((responseData, token, email, roles) => {
    const selectedRoles = normalizeRoles(roles);
    const selectedUserData = {
      ...(responseData || {}),
      userRoles: selectedRoles,
      userRole: selectedRoles[0] || responseData?.userRole,
    };

    finalizeAuthenticatedSession({
      token,
      email,
      roles: selectedRoles,
      userData: selectedUserData,
    });

    dispatch({
      type: types.SIGNIN_SUCCESS,
      payload: {
        ...selectedUserData,
        data: selectedUserData,
        token,
        email,
        userRoles: selectedRoles,
        userRole: selectedRoles[0],
        isAuthenticated: true,
        isLoggedIn: true,
      },
    });
  }, [dispatch]);

  const clearStagedLoginSession = useCallback(() => {
    clearLoginTransition();
    clearConsoleHandoffData();
    localStorage.removeItem("token");
    localStorage.removeItem("email");
    localStorage.removeItem("userRoles");
    localStorage.removeItem("userData");
    localStorage.removeItem("isLoggedIn");
    localStorage.removeItem("isAuthenticated");
    clearPendingNavigation();
    sessionStorage.removeItem("googleAuthenticated");
    window.dispatchEvent(new Event("avps-auth-updated"));
    setSelectedRole("");
    setUserRoles([]);
    setEligibleRoleOptions([]);
    setSelectedRoleOptionKey("");
    setPendingLoginSession(null);
    setShowRoleChoiceModal(false);
    setShowEmployeeChoiceModal(false);
    setLoadingAction(null);
    setIsLoading(false);
    dispatch({ type: types.CLEAR_PENDING_AUTH });
    if (window.location.pathname !== "/sign-in") {
      navigate("/sign-in", { replace: true });
    }
  }, [dispatch, navigate]);

  const buildConsoleRedirectUrl = useCallback((dashboardUrl, fallbackCompanyCode = companyPrimaryId) => {
    const handoffData = getConsoleHandoffData();

    if (!handoffData?.encryptedEmail) {
      throw new Error("Login handoff data is missing. Please sign in again.");
    }

    return appendConsoleHandoffParams(dashboardUrl, handoffData, fallbackCompanyCode);
  }, [companyPrimaryId]);

  const redirectToConsole = useCallback(async (dashboardUrl) => {
    if (!dashboardUrl) {
      throw new Error("Dashboard URL is not configured.");
    }

    setLoginTransition("console-redirect");
    const redirectUrl = buildConsoleRedirectUrl(dashboardUrl);

    window.location.assign(redirectUrl);
  }, [buildConsoleRedirectUrl]);

  const handleEmployeeChoice = async (choice) => {
    setShowEmployeeChoiceModal(false);
    if (choice === "console") {
      try {
        const companyString = localStorage.getItem("company");
        if (!companyString) {
          setError("Company details not found. Please try logging in again.");
          return;
        }

        let company;
        try {
          company = JSON.parse(companyString);
        } catch {
          setError("Invalid company data. Please try logging in again.");
          return;
        }

        const prefix = company?.urlPrefix || "";
        const nextCompanyCode = company?.primaryId || "";
        const consolePath = getConsoleReturnUrl() || "/console/manage";
        const consoleURL = `${getConsoleBaseUrl(company?.url, prefix)}${consolePath}`;
        const handoffData = getConsoleHandoffData();

        if (!handoffData?.encryptedEmail) {
          setError("Session handoff expired. Please login again.");
          return;
        }

        storeConsoleHandoffData({
          ...handoffData,
          companyCode: nextCompanyCode,
        });
        await redirectToConsole(consoleURL);
      } catch (redirectError) {
        setError(redirectError?.message || "Error redirecting. Please try again.");
        clearLoginTransition();
      }
    } else if (choice === "pos") {
      clearConsoleHandoffData();
      persistSessionForAppRole(
        pendingLoginSession?.userData || JSON.parse(localStorage.getItem("userData") || "{}"),
        pendingLoginSession?.token || localStorage.getItem("token"),
        pendingLoginSession?.email || localStorage.getItem("email"),
        ["EMPLOYEE"]
      );
      setPendingLoginSession(null);
      navigate("/pos-dashboard");
    }
  };

  const handleRoleSelection = (option) => {
    setSelectedRole(option.role);
    setSelectedRoleOptionKey(getRoleOptionKey(option.role, option.company));
    setError("");
  };

  const handleRoleChoice = async () => {
    const selectedOption = eligibleRoleOptions.find(
      (option) => getRoleOptionKey(option.role, option.company) === selectedRoleOptionKey
    ) || (selectedRole ? { role: selectedRole, company: null } : null);

    if (!selectedOption) {
      setError("Please select a role to continue");
      return;
    }

    setShowRoleChoiceModal(false);
    const currentUserData = pendingLoginSession?.userData || JSON.parse(localStorage.getItem("userData") || "{}");
    const currentToken = pendingLoginSession?.token || localStorage.getItem("token");
    const currentEmail = pendingLoginSession?.email || localStorage.getItem("email");

    const selectedCompany = selectedOption.company;
    const selectedCompanyId = selectedCompany?.primaryId || selectedCompany?.companyId || companyPrimaryId;
    const isDifferentCompany = Boolean(
      selectedCompany
      && selectedCompanyId
      && String(selectedCompanyId) !== String(companyPrimaryId)
    );
    const isConsoleRole = CONSOLE_ROLES.includes(selectedOption.role);

    let processedCompanyUrl = selectedCompany?.url || companyUrl;
    if (processedCompanyUrl && processedCompanyUrl.includes(":3000")) {
      processedCompanyUrl = processedCompanyUrl.replace(":3000", ":4000");
    }

    // In-app roles (Student/Customer/Applicant/Candidate/Employee) that belong to
    // a different company than the one already authenticated must re-authenticate
    // against that specific company — the already-fetched session data belongs to
    // the original company and must never be reused as if it were the new one.
    let sessionUserData = currentUserData;
    let sessionToken = currentToken;
    if (!isConsoleRole && isDifferentCompany) {
      try {
        const handoffData = getConsoleHandoffData();
        const reAuthEmail = decodeBase64(handoffData?.encryptedEmail || "") || currentEmail;
        const reAuthPassword = decodeBase64(handoffData?.encryptedPassword || "");
        const reAuthResponse = await dispatch(signin({
          email: reAuthEmail,
          password: reAuthPassword,
          company: { primaryId: selectedCompanyId },
        }));
        sessionUserData = getLoginResponseData(reAuthResponse) || currentUserData;
        sessionToken = getLoginResponseToken(reAuthResponse) || currentToken;
        setPendingLoginSession((prev) => ({ ...(prev || {}), userData: sessionUserData, token: sessionToken }));
      } catch (reAuthError) {
        setError(reAuthError?.message || "Unable to switch company context. Please try again.");
        return;
      }
    }

    const pendingTarget = getPendingNavigation();
    if (pendingTarget) {
      persistSessionForAppRole(sessionUserData, sessionToken, currentEmail, [selectedOption.role]);
      consumePendingNavigation();
      navigate(pendingTarget, { replace: true });
      return;
    }

    switch (selectedOption.role) {
      case "CUSTOMER":
        persistSessionForAppRole(sessionUserData, sessionToken, currentEmail, ["CUSTOMER"]);
        if (domainType === "FLEX_OFFICE") {
          const pendingBooking = sessionStorage.getItem("pendingBooking");
          if (pendingBooking) {
            try {
              const bookingData = JSON.parse(pendingBooking);
              navigate("/selected-product-details", { state: bookingData });
            } catch {
              navigate("/flex-home");
            }
          } else {
            navigate("/flex-home");
          }
        } else {
          navigate("/");
        }
        break;

      case "STUDENT":
        persistSessionForAppRole(sessionUserData, sessionToken, currentEmail, ["STUDENT"]);
        navigate(domainType === "EDUCATION" ? "/" : "/user-profile", { replace: true });
        break;

      case "ADMIN":
      case "INSTRUCTOR":
      case "TEACHING_ASSISTANT":
      case "SUPER_ADMIN": {
        const consolePath = getConsoleReturnUrl() || "/console/manage";
        const dashboardUrl = `${getConsoleBaseUrl(processedCompanyUrl, selectedCompany?.urlPrefix)}${consolePath}`;
        const handoffData = getConsoleHandoffData();
        storeConsoleHandoffData({
          ...(handoffData || {}),
          companyCode: selectedCompanyId,
        });
        redirectToConsole(dashboardUrl).catch((redirectError) => {
          setError(redirectError?.message || "Unable to open console. Please try again.");
          clearLoginTransition();
        });
        break;
      }

      case "EMPLOYEE":
        if (domainType === "RETAIL") {
          setLoginTransition("employee-choice");
          setShowEmployeeChoiceModal(true);
        } else {
          persistSessionForAppRole(sessionUserData, sessionToken, currentEmail, ["EMPLOYEE"]);
          navigate("/");
        }
        break;

      case "APPLICANT":
        persistSessionForAppRole(sessionUserData, sessionToken, currentEmail, ["APPLICANT"]);
        navigate("/user-profile", { replace: true });
        break;

      case "CANDIDATE":
        persistSessionForAppRole(sessionUserData, sessionToken, currentEmail, ["CANDIDATE"]);
        navigate("/system-check", { replace: true });
        break;

      default:
        clearLoginTransition();
        navigate("/");
        break;
    }
  };

  // A single sign-in only ever authenticates against one company, so it can
  // only ever return that company's roles. To detect every role a person
  // holds (e.g. Partner Admin in a partner company AND Student in the
  // parent), probe every other company in their "family" (parent + partner
  // companies) with the same credentials via the existing sign-in endpoint,
  // and keep whichever attempts succeed. Companies with no matching user for
  // this email simply fail and are skipped — expected for most people.
  const collectEligibleRoleOptions = async (authCompany, authRoles, email, password) => {
    const authMemberships = [{ company: authCompany || null, userRoles: authRoles }];

    if (authMemberships.length) {
      return flattenMembershipsToRoleOptions(authMemberships);
    }

    if (!email || !password) {
      return flattenMembershipsToRoleOptions(authMemberships);
    }

    try {
      const authCompanyId = authCompany?.primaryId || authCompany?.companyId;
      const parentCompany = isPartnerCompanyRecord(authCompany)
        ? await dispatch(findCompanyById(authCompany?.parentCompanyId))
        : authCompany;

      const parentCompanyId = parentCompany?.primaryId || parentCompany?.companyId || authCompany?.parentCompanyId;
      if (!parentCompanyId) {
        return flattenMembershipsToRoleOptions(authMemberships);
      }

      const partners = await dispatch(findPartnerCompanies(parentCompanyId));
      const familyCompanies = [parentCompany, ...(Array.isArray(partners) ? partners : [])].filter(Boolean);

      const candidateCompanies = familyCompanies.filter((candidate) => {
        const candidateId = candidate?.primaryId || candidate?.companyId;
        return candidateId && String(candidateId) !== String(authCompanyId);
      });

      const candidateResults = await Promise.all(
        candidateCompanies.map(async (candidate) => {
          const candidateId = candidate?.primaryId || candidate?.companyId;
          try {
            const candidateResponse = await dispatch(signin({
              email,
              password,
              company: { primaryId: candidateId },
            }));
            const candidateRoles = getLoginResponseRoles(candidateResponse);
            if (!candidateRoles.length) return null;
            return { company: candidate, userRoles: candidateRoles };
          } catch {
            // No matching user in this company for this email — expected, skip it.
            return null;
          }
        })
      );

      return flattenMembershipsToRoleOptions([
        ...authMemberships,
        ...candidateResults.filter(Boolean),
      ]);
    } catch {
      // Discovery is best-effort — if anything fails, fall back to the single
      // company already authenticated against, matching today's behavior.
      return flattenMembershipsToRoleOptions(authMemberships);
    }
  };

  const handleSuccessfulLogin = async (response, emailOverride = null, passwordOverride = null) => {
    const responseData = getLoginResponseData(response);
    const responseRoles = getLoginResponseRoles(response);
    const token = getLoginResponseToken(response);
    const userEmail = emailOverride || responseData?.email || user?.email;
    const userPassword = passwordOverride || user?.password;
    // Every company membership (role + its own company) for this email —
    // discovered by probing the parent + partner companies with the same
    // credentials. Resolves to just this one company/role when there's no
    // partner relationship involved, so single-membership logins are unaffected.
    const discoveredOptions = await collectEligibleRoleOptions(
      responseData?.company || companyData,
      responseRoles,
      userEmail,
      userPassword
    );
    const eligibleOptions = normalizeRoleOptionsForDomain(discoveredOptions, domainType);

    if (!eligibleOptions.length && discoveredOptions.length) {
      clearLoginTransition();
      setError("This account does not have access for this company domain.");
      setLoadingAction(null);
      setIsLoading(false);
      return;
    }
    const effectiveRoles = eligibleOptions.length
      ? [...new Set(eligibleOptions.map((option) => option.role))]
      : responseRoles;

    if (eligibleOptions.length > 1) {
      try {
        setLoginTransition("role-selection");
        storeConsoleHandoffData({
          encryptedEmail: encodeBase64(userEmail || ""),
          encryptedPassword: encodeBase64(userPassword || ""),
          companyCode: companyPrimaryId,
        });
        localStorage.removeItem("token");
        localStorage.removeItem("email");
        localStorage.removeItem("userRoles");
        localStorage.removeItem("userData");
        localStorage.removeItem("isLoggedIn");
        localStorage.removeItem("isAuthenticated");
        window.dispatchEvent(new Event("avps-auth-updated"));
      } catch (storageError) {
        setError(storageError?.message || "Unable to prepare login session. Please try again.");
        clearLoginTransition();
        setLoadingAction(null);
        setIsLoading(false);
        return;
      }

      setPendingLoginSession({
        token,
        email: userEmail,
        roles: effectiveRoles,
        userData: responseData,
      });
      setEligibleRoleOptions(eligibleOptions);
      setUserRoles([...new Set(eligibleOptions.map((option) => option.role))]);
      setSelectedRole("");
      setSelectedRoleOptionKey("");
      setShowRoleChoiceModal(true);
      setLoadingAction(null);
      setIsLoading(false);
      return;
    }

    try {
      stageAuthenticatedSession({
        token,
        email: userEmail,
        roles: effectiveRoles,
        userData: responseData,
      });
      storeConsoleHandoffData({
        encryptedEmail: encodeBase64(userEmail || ""),
        encryptedPassword: encodeBase64(userPassword || ""),
        companyCode: companyPrimaryId,
      });
    } catch (storageError) {
      setError(storageError?.message || "Unable to prepare login session. Please try again.");
      clearLoginTransition();
      setLoadingAction(null);
      setIsLoading(false);
      return;
    }

    setUserRoles(effectiveRoles);

    const singleRole = effectiveRoles[0];
    const redirectPath = getPendingNavigation();

    if (redirectPath) {
      persistSessionForAppRole(responseData, token, userEmail, effectiveRoles);
      consumePendingNavigation();
      navigate(redirectPath, { replace: true });
      return;
    }

    switch (singleRole) {
      case "STUDENT":
        persistSessionForAppRole(responseData, token, userEmail, effectiveRoles);
        navigate("/", { replace: true });
        break;

      case "APPLICANT":
        persistSessionForAppRole(responseData, token, userEmail, effectiveRoles);
        navigate("/user-profile", { replace: true });
        break;

      case "CUSTOMER":
        persistSessionForAppRole(responseData, token, userEmail, effectiveRoles);
        if (domainType === "FLEX_OFFICE") {
          const pendingBooking = sessionStorage.getItem("pendingBooking");
          if (pendingBooking) {
            try {
              const bookingData = JSON.parse(pendingBooking);
              navigate("/selected-product-details", { state: bookingData });
            } catch {
              navigate("/flex-home");
            }
          } else {
            navigate("/flex-home");
          }
        } else {
          navigate("/");
        }
        break;

      case "CANDIDATE":
        persistSessionForAppRole(responseData, token, userEmail, effectiveRoles);
        navigate("/system-check", { replace: true });
        break;

      case "ADMIN":
      case "INSTRUCTOR":
      case "TEACHING_ASSISTANT":
      case "SUPER_ADMIN": {
        let processedCompanyUrl = companyUrl;
        if (companyUrl && companyUrl.includes(":3000")) {
          processedCompanyUrl = companyUrl.replace(":3000", ":4000");
        }
        const consolePath = getConsoleReturnUrl() || "/console/manage";
        const dashboardUrl = `${getConsoleBaseUrl(processedCompanyUrl)}${consolePath}`;
        await redirectToConsole(dashboardUrl);
        break;
      }

      case "EMPLOYEE":
        if (domainType === "RETAIL") {
          setLoginTransition("employee-choice");
          setShowEmployeeChoiceModal(true);
        } else {
          persistSessionForAppRole(responseData, token, userEmail, responseRoles);
          navigate("/");
        }
        break;

      default:
        clearLoginTransition();
        navigate("/");
        break;
    }
  };

  const handleGoogleCredentialResponse = async (response) => {
    setLoadingAction("google");
    setIsLoading(true);
    setError("");

    const idToken = response?.credential;

    try {
      const payload = JSON.parse(atob(idToken.split('.')[1]));
      const googleEmail = payload?.email;

      if (!googleEmail) {
        throw new Error("Email not received from Google");
      }

      const signinPayload = {
        email: googleEmail,
        authProvider: "GOOGLE",
        userRoles: isRetailDomain ? ["CUSTOMER"] : ["STUDENT"],
        authToken: idToken,
        company: { primaryId: companyPrimaryId },
      };

      const resultAction = await dispatch(googleSignin(signinPayload));

      const isSuccess =
        resultAction?.type?.includes('SUCCESS') ||
        resultAction?.payload?.status === 200 ||
        resultAction?.status === 200 ||
        resultAction?.data?.status === 200 ||
        resultAction?.payload?.message?.toLowerCase().includes("success") ||
        resultAction?.payload?.message?.toLowerCase().includes("login successful") ||
        resultAction?.message?.toLowerCase().includes("success");

      if (isSuccess) {
        const responseData = resultAction?.payload?.data ||
          resultAction?.data ||
          resultAction?.payload ||
          resultAction;

        await handleSuccessfulLogin(responseData, googleEmail);
      } else {
        const errorMessage =
          resultAction?.payload?.message ||
          resultAction?.error?.message ||
          resultAction?.message ||
          resultAction?.payload?.error?.message ||
          "Google login failed. Please try again.";

        throw new Error(errorMessage);
      }
    } catch (err) {
      setError(err?.message || "Google login error. Please try again.");
      setLoadingAction(null);
      setIsLoading(false);
    }
  };
  handleGoogleCredentialResponseRef.current = handleGoogleCredentialResponse;

  const handleNativeGoogleSignIn = async () => {
    setLoadingAction("google");
    setIsLoading(true);
    setError("");
    try {
      const idToken = await signInWithGoogleNative();
      await handleGoogleCredentialResponseRef.current?.({ credential: idToken });
    } catch (err) {
      if (err?.message !== GOOGLE_SIGNIN_CANCELLED) {
        setError(err?.message || "Google login error. Please try again.");
      }
      setLoadingAction(null);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!isEducationDomain && !isRetailDomain) {
      return undefined;
    }
    if (isNativeMobileApp()) {
      return undefined;
    }

    const initializeGoogleOneTap = () => {
      if (!window.google || !window.google.accounts) {
        return;
      }

      try {
        window.google.accounts.id.initialize({
          client_id: GOOGLE_CLIENT_ID,
          callback: (response) => handleGoogleCredentialResponseRef.current?.(response),
          auto_select: false,
          cancel_on_tap_outside: true,
          context: "signin"
        });

        const googleButtonElement = document.getElementById("googleOneTapButton");
        if (googleButtonElement) {
          const measuredWidth = googleButtonElement.getBoundingClientRect().width || googleButtonElement.offsetWidth;
          const viewportFallback = typeof window !== "undefined" ? window.innerWidth - 48 : 360;
          const buttonWidth = Math.min(430, Math.max(280, Math.floor(measuredWidth || viewportFallback || 360)));

          googleButtonElement.innerHTML = "";
          window.google.accounts.id.renderButton(
            googleButtonElement,
            {
              type: "standard",
              theme: "outline",
              size: "large",
              text: "signin_with",
              shape: "rectangular",
              logo_alignment: "left",
              width: buttonWidth
            }
          );
        }

        setTimeout(() => {
          if (!(isAuthenticated || isLoggedIn)) {
            window.google.accounts.id.prompt();
          }
        }, 1000);
      } catch {
        setGoogleScriptLoaded(false);
      }
    };

    if (window.google) {
      setGoogleScriptLoaded(true);
      initializeGoogleOneTap();
      return undefined;
    }

    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.onload = () => {
      setGoogleScriptLoaded(true);
      initializeGoogleOneTap();
    };
    script.onerror = () => {
      setError('Failed to load Google authentication');
    };
    document.head.appendChild(script);

    return () => {
      const existingScript = document.querySelector('script[src="https://accounts.google.com/gsi/client"]');
      if (existingScript) {
        existingScript.remove();
      }
    };
  }, [isAuthenticated, isEducationDomain, isLoggedIn, isRetailDomain]);

  useEffect(() => {
    if (!otpSent || otpCountdown <= 0) {
      return undefined;
    }

    const timer = window.setTimeout(() => {
      setOtpCountdown((prev) => Math.max(prev - 1, 0));
    }, 1000);

    return () => window.clearTimeout(timer);
  }, [otpCountdown, otpSent]);

  const handleSubmit = async (e) => {
    e?.preventDefault();

    if (!user?.email || !user?.password) {
      setError("Email and password are required.");
      return;
    }

    setLoadingAction("password");
    setIsLoading(true);
    setError("");
    setSuccess("");

    try {
      const response = await dispatch(
        signin({
          email: user.email,
          password: user.password,
          company: { primaryId: companyPrimaryId }
        })
      );

      const isSuccess = (
        response?.status === 200 ||
        response?.message?.includes('Login successful')
      );

      if (isSuccess) {
        const userRoles = getLoginResponseRoles(response);
        setUserRoles(userRoles);

        await handleSuccessfulLogin(response, user.email, user.password);
      } else {
        if (response?.message && !response?.message?.toLowerCase().includes('success')) {
          setError(response.message);
        } else {
          setError("Login failed. Please check your credentials.");
        }
      }
    } catch (err) {
      if (err?.response?.data?.message) {
        setError(err.response.data.message);
      } else if (err?.response?.status === 401) {
        setError("Invalid email or password.");
      } else if (err?.response?.status === 400) {
        setError("Bad request. Please check your input.");
      } else if (err?.response?.status >= 500) {
        setError("Server error. Please try again later.");
      } else {
        setError("Login failed. Please try again.");
      }
    } finally {
      setLoadingAction(null);
      setIsLoading(false);
    }
  };

  const handleSendOtp = async () => {
    if (!validatePhone(phoneNumber)) {
      setError("Please enter a valid 10-digit phone number.");
      setSuccess("");
      return;
    }

    setLoadingAction("otp-send");
    setIsLoading(true);
    setError("");
    setSuccess("");

    try {
      const response = await dispatch(
        sendOtp({
          phoneNumber,
          deleted: false,
          company: { primaryId: companyPrimaryId }
        }, "Phone_otp")
      );

      if (response) {
        setOtpSent(true);
        setOtp("");
        setOtpCountdown(60);
        setSuccess("OTP sent successfully to your phone");
      }
    } catch (err) {
      setError(err?.message || "Failed to send OTP. Please try again.");
    } finally {
      setLoadingAction(null);
      setIsLoading(false);
    }
  };

  const handleOtpLogin = async (e) => {
    e?.preventDefault();

    if (!validatePhone(phoneNumber)) {
      setError("Please enter a valid 10-digit phone number.");
      setSuccess("");
      return;
    }

    if (!otp || otp.length !== 6) {
      setError("Please enter a valid 6-digit OTP.");
      setSuccess("");
      return;
    }

    setLoadingAction("otp-login");
    setIsLoading(true);
    setError("");
    setSuccess("");

    try {
      const response = await dispatch(
        OtpLogin({
          phoneNumber,
          dynamicAttributes: [{
            name: "Phone_otp",
            value: otp
          }],
          company: { primaryId: companyPrimaryId }
        }, "Phone_otp")
      );

      if (response?.status === 200 || response?.message?.includes("Login successful")) {
        await handleSuccessfulLogin(response);
      } else {
        setError(response?.message || "OTP verification failed.");
      }
    } catch (err) {
      setError(err?.message || "OTP verification failed. Please try again.");
    } finally {
      setLoadingAction(null);
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setState({ open: false, message: "", severity: "error" });
  };

  const handleForgetPassword = () => {
    navigate("/forgot-password");
  };

  const handleForgetEmail = () => {
    navigate("/forgot-email");
  };

  const signup = (e) => {
    e?.preventDefault();
    navigate("/sign-up");
  };

  const userString = localStorage.getItem('user');
  let fullName = "Guest";
  if (userString) {
    try {
      const user = JSON.parse(userString);
      fullName = `${user?.userDetails?.firstName || ""} ${user?.userDetails?.lastName || ""}`.trim() || "Guest";
    } catch {
      fullName = "Guest";
    }
  }

  useEffect(() => {
    const fullUrl = window?.location?.href || "";
    const subdomain = fullUrl.split("/").slice(0, 3).join("/");
    if (subdomain) {
      // getCompanyDetails function would be here
    }
  }, []);

  useEffect(() => {
    if (isLoginTransitionActive(["role-selection"])) {
      try {
        const stagedRoles = normalizeRoles(JSON.parse(localStorage.getItem("userRoles") || "[]"));
        if (stagedRoles.length > 1) {
          setUserRoles(stagedRoles);
          setSelectedRole("");
          setShowRoleChoiceModal(true);
          setShowEmployeeChoiceModal(false);
          setLoadingAction(null);
          setIsLoading(false);
        }
      } catch {
        clearStagedLoginSession();
        setError("Your sign-in session could not be restored. Please sign in again.");
      }

      return;
    }

    if (isLoginTransitionActive(["employee-choice"])) {
      setShowRoleChoiceModal(false);
      setShowEmployeeChoiceModal(true);
      setLoadingAction(null);
      setIsLoading(false);
    }
  }, [clearStagedLoginSession]);

  useEffect(() => {
    const cond = localStorage.getItem("isLoggedIn");
    const tokenExpired = localStorage.getItem("isTokenExpired") || location.state?.isTokenExpired;
    if (tokenExpired) {
      setState({
        open: true,
        message: SESSION_EXPIRED_MESSAGE,
        severity: "error",
      });
      localStorage.removeItem("isTokenExpired");
    }

    if (!cond || isLoginTransitionActive()) {
      return;
    }

    const existingRoles = normalizeRoles(JSON.parse(localStorage.getItem("userRoles") || "[]"));
    const hasConsoleRole = existingRoles.some((role) => CONSOLE_ROLES.includes(role));

    if (hasConsoleRole) {
      return;
    }

    const preferredRole = existingRoles.find((role) => IN_APP_ROLES.includes(role)) || existingRoles[0];

    if (preferredRole === "CUSTOMER" && domainType === "FLEX_OFFICE") {
      navigate("/flex-home", { replace: true });
      return;
    }

    navigate("/", { replace: true });
  }, [domainType, location.state?.isTokenExpired, navigate]);

  const isFlexOffice = domainType === "FLEX_OFFICE";
  const supportsOtpLogin = isEducationDomain || isRetailDomain;

  if (isFlexOffice) {
    return <FlexLogin templateDomainType={domainType} />;
  }

  const eligibleRoleOptionCompanyCount = new Set(
    eligibleRoleOptions
      .map((option) => option.company?.primaryId || option.company?.companyId || "")
      .filter(Boolean)
  ).size;

  return (
    <div className={isRetailDomain ? "retail-auth-theme" : undefined}>
      {showRoleChoiceModal && createPortal(
        <div
          className="fixed inset-0 z-50 flex items-center justify-center px-4 py-6"
          style={{ position: 'fixed', inset: 0, width: '100vw', height: '100dvh', borderRadius: 0, clipPath: 'none', overflow: 'hidden', transform: 'none' }}
        >
          <button
            type="button"
            aria-label="Close role selection"
            className="absolute inset-0 h-full w-full cursor-default bg-gray-900/40"
            style={{ borderRadius: 0, clipPath: 'none' }}
            onClick={clearStagedLoginSession}
          />
          <section
            role="dialog"
            aria-modal="true"
            aria-labelledby="role-choice-title"
            className="relative w-full max-w-sm rounded-xl bg-white p-6 shadow-2xl"
            style={{ borderRadius: '14px', clipPath: 'none' }}
          >
            <h2 id="role-choice-title" className="text-center text-2xl font-semibold text-gray-900">
              Choose Your Role
            </h2>
            <p className="mt-3 text-center text-sm text-gray-600">
              You have multiple roles. Please select one to continue.
            </p>

            <div className="mt-6 space-y-3">
              {eligibleRoleOptions.map((option) => {
                const optionKey = getRoleOptionKey(option.role, option.company);
                const isSelected = selectedRoleOptionKey === optionKey;
                const showCompanyTag = eligibleRoleOptionCompanyCount > 1;

                return (
                  <button
                    type="button"
                    key={optionKey}
                    onClick={() => handleRoleSelection(option)}
                    className={`w-full rounded-lg border px-4 py-3 text-left transition focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      isSelected
                        ? "border-blue-600 bg-blue-50"
                        : "border-gray-300 bg-white hover:border-blue-500 hover:bg-blue-50/50"
                    }`}
                  >
                    <span className="block text-sm font-semibold text-gray-900">
                      {getRoleLabel(option.role)}
                    </span>
                    <span className="mt-1 block text-xs text-gray-500">
                      {getRoleDescription(option.role, domainType)}
                    </span>
                    {showCompanyTag && (
                      <span className="mt-1 block text-xs font-semibold text-blue-700">
                        {getCompanyContextLabel(option.company)}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>

            <StatusMessage severity="error" className="mt-4">
              {error}
            </StatusMessage>

            <div className="mt-6 flex gap-3">
              <button
                type="button"
                onClick={clearStagedLoginSession}
                className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm font-semibold text-gray-700 transition hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleRoleChoice}
                disabled={!selectedRoleOptionKey}
                className="w-full rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              >
                Continue
              </button>
            </div>
          </section>
        </div>,
        document.body
      )}

      {showEmployeeChoiceModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4 py-6">
          <button
            type="button"
            aria-label="Close employee choice"
            className="absolute inset-0 h-full w-full cursor-default bg-gray-900/40"
            onClick={clearStagedLoginSession}
          />
          <section
            role="dialog"
            aria-modal="true"
            aria-labelledby="employee-choice-title"
            className="relative w-full max-w-sm rounded-2xl bg-white p-6 text-center shadow-2xl"
          >
            <h2 id="employee-choice-title" className="text-xl font-semibold text-gray-900">
              Welcome {fullName}
            </h2>
            <p className="mt-3 text-sm text-gray-600">How would you like to continue?</p>

            <div className="mt-6 space-y-3">
              <button
                type="button"
                onClick={() => handleEmployeeChoice("console")}
                className="w-full rounded-lg bg-blue-600 px-4 py-3 text-sm font-semibold text-white transition hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              >
                Console
              </button>
              <button
                type="button"
                onClick={() => handleEmployeeChoice("pos")}
                className="w-full rounded-lg bg-amber-500 px-4 py-3 text-sm font-semibold text-white transition hover:bg-amber-600 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-2"
              >
                POS
              </button>
            </div>

            <button
              type="button"
              onClick={clearStagedLoginSession}
              className="mt-5 w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm font-semibold text-gray-700 transition hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              Cancel
            </button>
          </section>
        </div>
      )}

      {/* Login Form */}
      <div
        className="max-w-md mx-auto p-5 sm:p-8 bg-white custom-shadow rounded-lg mb-10"
        style={{ marginTop: "calc(var(--layout-header-height, 70px) + 2.5rem)" }}
      >
        {state?.open && (
          <StatusMessage severity={state?.severity} onClose={handleClose} className="mb-3">
            {state?.message}
          </StatusMessage>
        )}

        <div className="text-center mb-8" style={{ marginTop: "50px" }}>
          <h1 className="text-2xl font-bold text-gray-900">
            {localStorage.getItem("domainType") !== "ASSESSMENT"
              ? "Hi, Welcome Back"
              : "Hi, Please Sign In"}
          </h1>

          {error && (
            <StatusMessage severity="error" className="mt-4">
              {error}
            </StatusMessage>
          )}

          {success && (
            <StatusMessage severity="success" className="mt-4">
              {success}
            </StatusMessage>
          )}
        </div>

        {supportsOtpLogin ? (
          <>
            <div className="mb-4 grid grid-cols-2 border-b border-gray-200">
              {["Email", "OTP"].map((label, index) => (
                <button
                  type="button"
                  key={label}
                  onClick={() => {
                    setActiveTab(index);
                    setError("");
                    setSuccess("");
                    setOtp("");
                    setOtpSent(false);
                    setOtpCountdown(0);
                  }}
                  className={`min-h-12 border-b-[3px] px-4 py-3 text-sm font-medium transition focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                    activeTab === index
                      ? "border-blue-500 text-blue-600"
                      : "border-transparent text-gray-500 hover:text-gray-700"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>

            {activeTab === 0 && (
              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label htmlFor="login-email" className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address
                  </label>
                  <input
                    id="login-email"
                    name="email"
                    type="email"
                    value={user?.email || ""}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    required
                    className={`w-full px-4 py-3.5 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 text-gray-700 ${emailError ? "border-red-500" : "border-gray-200"
                      }`}
                    placeholder="you@example.com"
                  />
                  {emailError && (
                    <p className="text-red-500 text-sm mt-1">
                      Please enter a valid email address
                    </p>
                  )}
                </div>

                <div>
                  <label htmlFor="login-password" className="block text-sm font-medium text-gray-700 mb-2">
                    Password
                  </label>
                  <div className="relative">
                    <input
                      id="login-password"
                      name="password"
                      type={showPassword ? "text" : "password"}
                      value={user?.password || ""}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent pr-12 transition-all duration-200 text-gray-700"
                      placeholder="Enter your password"
                    />
                    <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                      <PasswordToggleButton
                        showPassword={showPassword}
                        onToggle={handleClickShowPassword}
                        onMouseDown={handleMouseDownPassword}
                      />
                    </div>
                  </div>
                </div>

                <div className="flex justify-between items-center text-sm">
                  <button
                    type="button"
                    onClick={handleForgetEmail}
                    className="text-blue-600 hover:text-blue-700 font-medium transition-colors hover:underline"
                  >
                    Forgot Email?
                  </button>
                  <button
                    type="button"
                    onClick={handleForgetPassword}
                    className="text-blue-600 hover:text-blue-700 font-medium transition-colors hover:underline"
                  >
                    Forgot Password?
                  </button>
                </div>

                <button
                  id="sign-in-button"
                  type="submit"
                  disabled={isLoading}
                  className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-semibold py-3.5 rounded-xl transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed shadow-lg shadow-blue-500/30 hover:shadow-xl hover:shadow-blue-500/40 transform hover:-translate-y-0.5"
                >
                  {isLoading ? (
                    <span className="flex items-center justify-center">
                      <Loader2 className="mr-2 h-5 w-5 animate-spin" aria-hidden="true" />
                      Signing In...
                    </span>
                  ) : (
                    "Sign In"
                  )}
                </button>
              </form>
            )}

            {activeTab === 1 && (
              <form onSubmit={handleOtpLogin} className="space-y-5">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Phone Number
                  </label>
                  <div className="relative">
                    <div className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-500 font-semibold">
                      {countryCallingCode}
                    </div>
                    <input
                      type="tel"
                      value={phoneNumber}
                      onChange={handlePhoneChange}
                      disabled={otpSent}
                      required
                      className="w-full pl-16 pr-4 py-3.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 disabled:bg-gray-50 text-gray-700"
                      placeholder="Enter 10-digit number"
                    />
                  </div>
                </div>

                {!otpSent ? (
                  <button
                    type="button"
                    onClick={handleSendOtp}
                    disabled={isLoading || phoneNumber.length < 10}
                    className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-semibold py-3.5 rounded-xl transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed shadow-lg shadow-blue-500/30 hover:shadow-xl hover:shadow-blue-500/40 transform hover:-translate-y-0.5"
                  >
                    {isLoading ? (
                      <span className="flex items-center justify-center">
                        <Loader2 className="mr-2 h-5 w-5 animate-spin" aria-hidden="true" />
                        Sending OTP...
                      </span>
                    ) : (
                      "Send OTP"
                    )}
                  </button>
                ) : (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Enter OTP
                      </label>
                      <input
                        type="text"
                        value={otp}
                        onChange={handleOtpChange}
                        required
                        className="w-full px-4 py-3.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-center text-lg tracking-widest transition-all duration-200 text-gray-700 font-semibold"
                        placeholder="000000"
                        maxLength="6"
                      />
                      <div className="text-right mt-2">
                        {otpCountdown > 0 ? (
                          <span className="text-sm text-gray-500">
                            Resend OTP in <span className="font-semibold text-blue-600">{otpCountdown}s</span>
                          </span>
                        ) : (
                          <button
                            type="button"
                            onClick={handleSendOtp}
                            className="text-sm text-blue-600 hover:text-blue-700 font-medium transition-colors hover:underline"
                          >
                            Resend OTP
                          </button>
                        )}
                      </div>
                    </div>

                    <button
                      id="sign-in-button"
                      type="submit"
                      disabled={isLoading}
                      className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-semibold py-3.5 rounded-xl transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed shadow-lg shadow-blue-500/30 hover:shadow-xl hover:shadow-blue-500/40 transform hover:-translate-y-0.5"
                    >
                      {isLoading ? (
                        <span className="flex items-center justify-center">
                          <Loader2 className="mr-2 h-5 w-5 animate-spin" aria-hidden="true" />
                          Verifying OTP...
                        </span>
                      ) : (
                        "Verify & Sign In"
                      )}
                    </button>

                    <button
                      type="button"
                      onClick={() => {
                        setOtpSent(false);
                        setOtp("");
                        setPhoneNumber("");
                        setOtpCountdown(0);
                        setError("");
                        setSuccess("");
                      }}
                      className="w-full text-blue-600 hover:text-blue-700 font-medium py-2 text-sm transition-colors hover:underline"
                    >
                      &lt;- Use different phone number
                    </button>
                  </>
                )}
              </form>
            )}
          </>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="login-email" className="block text-sm font-semibold text-gray-700 mb-2">
                Email
              </label>
              <input
                id="login-email"
                name="email"
                type="email"
                value={user?.email || ""}
                onChange={handleChange}
                onBlur={handleBlur}
                required
                className={`w-full p-3 border rounded-lg shadow-sm transition duration-300 focus:outline-none focus:ring-2 focus:ring-blue-300 ${emailError ? "border-red-500" : "border-gray-300"
                  }`}
              />
              {emailError && (
                <p className="text-red-500 text-sm mt-1">
                  Please enter a valid email address
                </p>
              )}
            </div>

            <div>
              <label htmlFor="login-password" className="block text-sm font-semibold text-gray-700 mb-2">
                Password
              </label>
              <div className="relative">
                <input
                  id="login-password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  value={user?.password || ""}
                  onChange={handleChange}
                  required
                  className="w-full p-3 border rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
                />
                <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                  <PasswordToggleButton
                    showPassword={showPassword}
                    onToggle={handleClickShowPassword}
                    onMouseDown={handleMouseDownPassword}
                  />
                </div>
              </div>
            </div>

            <button
              id="sign-in-button"
              data-testid="sign-in-button"
              type="submit"
              disabled={isLoading}
              className="w-full rounded-lg bg-blue-600 px-4 py-3 text-base font-semibold text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            >
              {isLoading ? "Signing In..." : "Sign In"}
            </button>
          </form>
        )}

        {(isEducationDomain || isRetailDomain) && (
          <div className="mt-4">
            <div className="flex items-center gap-3 text-sm text-gray-500">
              <div className="h-px flex-1 bg-gray-200" />
              <span>or continue with</span>
              <div className="h-px flex-1 bg-gray-200" />
            </div>
            <div className="mt-4 flex justify-center">
              {isNativeMobileApp() ? (
                <GoogleAuthButton
                  onClick={handleNativeGoogleSignIn}
                  disabled={isLoading}
                />
              ) : (
                <div id="googleOneTapButton" className="w-full overflow-hidden rounded-xl">
                  {loadingAction === "google" && googleScriptLoaded && (
                    <div className="flex items-center justify-center py-2">
                      <span className="text-sm text-blue-600 font-medium">Signing in with Google...</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {!supportsOtpLogin && (
          <div className="mt-4 text-center">
            <button
              type="button"
              onClick={handleForgetEmail}
              className="font-medium text-blue-600 transition hover:text-blue-700 hover:underline"
            >
              Forgot Email
            </button>
            <span className="mx-2 text-gray-400">|</span>
            <button
              type="button"
              onClick={handleForgetPassword}
              className="font-medium text-blue-600 transition hover:text-blue-700 hover:underline"
            >
              Forgot Password
            </button>
          </div>
        )}

        {(["EDUCATION", "RETAIL", "SERVICES"].includes(localStorage.getItem("domainType"))) && (
          <div className="mt-4 text-center">
            <div className="border-t border-gray-300 my-4">
              <p className="text-gray-500">Or</p>
            </div>
            <p className="text-sm">
              Don't have an account?{" "}
              <button
                type="button"
                onClick={signup}
                className="font-semibold text-blue-600 transition hover:text-blue-700 hover:underline"
              >
                Sign Up
              </button>
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

function mapStateToProps(state) {
  const { isAuthenticated, user } = state?.auth?.data || {};
  return { isAuthenticated, user };
}

export default connect(mapStateToProps, { signin, googleSignin })(Login);

